import React, { useEffect } from "react";

import type { ICellEditorParams } from "ag-grid-community";

/**
 * Filters the selectable values of a dropdown cell editor for the current
 * surface.
 *
 * Receives the resolved `values` for a cell that is about to enter edit mode
 * plus its AG Grid `ICellEditorParams`, and returns the subset of values to
 * actually offer. This is the extension point for a surface that must hide
 * values which are valid in the stored data (and shown by other surfaces) but
 * not selectable here — e.g. a sentinel enum member that belongs to a
 * different tab. Returning the values unchanged is a no-op.
 */
export type EditorValuesFilter = (
  values: string[],
  params: ICellEditorParams,
) => string[];

// AG Grid calls `cellEditorSelector` as a plain function (not from a React
// component), so the active filter is held in a module-level ref that the
// provider wires up via render/unmount. Only one filter may be active at a
// time. Mirrors `CellEditorOverrideContext`.
let activeEditorValuesFilter: EditorValuesFilter | null = null;

/** Internal — read by `cellEditorSelector` in `buildColumns.ts`. */
export const getActiveEditorValuesFilter = (): EditorValuesFilter | null =>
  activeEditorValuesFilter;

interface EditorValuesFilterProviderProps {
  filter: EditorValuesFilter;
  children: React.ReactNode;
}

export const EditorValuesFilterProvider = ({
  filter,
  children,
}: EditorValuesFilterProviderProps): React.JSX.Element => {
  // Set synchronously during render so `cellEditorSelector` (called by AG Grid
  // on the same tick as a user click) always sees the current filter. A
  // useEffect would fire after paint, leaving a window where the module-level
  // ref is stale and cell clicks see unfiltered values.
  // eslint-disable-next-line react-compiler/react-compiler
  activeEditorValuesFilter = filter;

  // Cleanup on unmount only — runs after the component is removed from the tree.
  useEffect(
    () => () => {
      if (activeEditorValuesFilter === filter) {
        activeEditorValuesFilter = null;
      }
    },
    [filter],
  );

  return <>{children}</>;
};
